import store from '@/store';
import { getOrFetchLookup, invalidateLookupsMatching, lookupCacheKeys } from '@/utils/lookupCache';
import { resolveEffectiveLanguage } from '@/utils/language';
import { api, unwrap } from './http';

// Gender and currency names are localized by the server, so their cache keys carry the language.
const effectiveLanguageCode = () => resolveEffectiveLanguage(
  store.state && store.state.auth && store.state.auth.userData,
  store.state && store.state.settings && store.state.settings.tenantSetting,
).languageCode;

const cached = (key, endpoint, params, options) => getOrFetchLookup({
  key,
  fetcher: () => unwrap(api.get(endpoint, params)),
  ttlMs: options.ttlMs,
  forceRefresh: options.forceRefresh,
});

const afterMutation = async (promise, lookupPrefix) => {
  const result = await promise;
  invalidateLookupsMatching(lookupPrefix);
  return result;
};

const branchSettingsRequests = new Map();
const branchSettingSaveRequests = new Map();

const getBranchSettings = (id) => {
  const key = String(id);
  if (branchSettingsRequests.has(key)) return branchSettingsRequests.get(key);

  const request = unwrap(api.get(`/Branch/${key}/settings`))
    .finally(() => branchSettingsRequests.delete(key));
  branchSettingsRequests.set(key, request);
  return request;
};

const saveBranchSetting = (id, payload) => {
  const key = [
    String(id),
    payload.settingKey,
    payload.settingValue === null ? '' : String(payload.settingValue),
  ].join('|');
  if (branchSettingSaveRequests.has(key)) return branchSettingSaveRequests.get(key);

  const request = unwrap(api.put(`/Branch/${id}/settings`, payload))
    .finally(() => branchSettingSaveRequests.delete(key));
  branchSettingSaveRequests.set(key, request);
  return request;
};

export default {
  getCurrencies: async (params = {}, options = {}) => cached(
    lookupCacheKeys.currencies({ ...params, lang: effectiveLanguageCode() }), '/Currency', params, options,
  ),

  getAllCurrencies: async (params = {}, options = {}) => cached(
    lookupCacheKeys.allCurrencies({ ...params, lang: effectiveLanguageCode() }), '/Currency/all', params, options,
  ),

  // The shared currencies the club has not added yet. Not cached: adding one changes it.
  getCurrencyCatalog: async () => unwrap(api.get('/Currency/catalog')),

  getCurrencyById: async (id) => unwrap(api.get(`/Currency/${id}`)),

  // Note: POST uses lowercase `currency` per server API
  createCurrency: async (payload) => afterMutation(
    unwrap(api.post('/currency', payload)), 'lookups:currencies',
  ),

  updateCurrency: async (id, payload) => afterMutation(
    unwrap(api.put(`/Currency/${id}`, payload)), 'lookups:currencies',
  ),

  getGenders: async (params = {}, options = {}) => cached(
    lookupCacheKeys.genders({ ...params, lang: effectiveLanguageCode() }), '/Genders', params, options,
  ),

  getLevels: async (params = {}, options = {}) => cached(
    lookupCacheKeys.levels(params), '/Levels', params, options,
  ),

  getLevelById: async (id) => unwrap(api.get(`/Levels/${id}`)),

  createLevel: async (payload) => afterMutation(
    unwrap(api.post('/Levels', payload)), 'lookups:levels',
  ),

  updateLevel: async (id, payload) => afterMutation(
    unwrap(api.put(`/Levels/${id}`, payload)), 'lookups:levels',
  ),

  removeLevel: async (id) => afterMutation(
    unwrap(api.delete(`/Levels/${id}`)), 'lookups:levels',
  ),

  getDivisions: async (params = {}, options = {}) => cached(
    lookupCacheKeys.divisions(params), '/Divisions', params, options,
  ),

  getDivisionById: async (id) => unwrap(api.get(`/Divisions/${id}`)),

  createDivision: async (payload) => afterMutation(
    unwrap(api.post('/Divisions', payload)), 'lookups:divisions',
  ),

  updateDivision: async (id, payload) => afterMutation(
    unwrap(api.put(`/Divisions/${id}`, payload)), 'lookups:divisions',
  ),

  removeDivision: async (id) => afterMutation(
    unwrap(api.delete(`/Divisions/${id}`)), 'lookups:divisions',
  ),

  getBranches: async (params = {}, options = {}) => cached(
    lookupCacheKeys.branches(params), '/Branch', params, options,
  ),

  getBranchById: async (id) => unwrap(api.get(`/Branch/${id}`)),

  // The caller's resolved branch access for the active-branch switcher.
  getMyBranchAccess: async () => unwrap(api.get('/Branch/MyAccess')),

  createBranch: async (payload) => afterMutation(
    unwrap(api.post('/Branch', payload)), 'lookups:branches',
  ),

  updateBranch: async (id, payload) => afterMutation(
    unwrap(api.put(`/Branch/${id}`, payload)), 'lookups:branches',
  ),

  removeBranch: async (id) => afterMutation(
    unwrap(api.delete(`/Branch/${id}`)), 'lookups:branches',
  ),

  getRegions: async () => unwrap(api.get('/Region')),

  createRegion: async (payload) => unwrap(api.post('/Region', payload)),

  updateRegion: async (id, payload) => unwrap(api.put(`/Region/${id}`, payload)),

  removeRegion: async (id) => unwrap(api.delete(`/Region/${id}`)),

  // Coalesce concurrent callers only. The owning page still refetches after a successful update.
  getBranchSettings,

  // One key at a time; a blank value clears the override and the branch inherits again.
  saveBranchSetting,

  getBranchDocumentNumbering: async (id) => unwrap(api.get(`/Branch/${id}/document-numbering`)),

  saveBranchDocumentNumbering: async (id, payload) => unwrap(
    api.put(`/Branch/${id}/document-numbering`, payload),
  ),

  // ── FITNET-107: class activities + rooms lookups ──

  // Activity/category names are localized server-side (Accept-Language), so the cache
  // key must include the effective language, otherwise a language switch keeps serving
  // the stale (previous-language) catalog from localStorage.
  getActivities: async (params = {}, options = {}) => {
    // Default to the full catalog so no caller has to pass the perPage sentinel.
    const finalParams = { perPage: 99999, ...params };
    const { languageCode } = resolveEffectiveLanguage(
      store.state && store.state.auth && store.state.auth.userData,
      store.state && store.state.settings && store.state.settings.tenantSetting,
    );
    return cached(
      lookupCacheKeys.activities({ ...finalParams, lang: languageCode }), '/Activities', finalParams, options,
    );
  },

  getRooms: async (params = {}, options = {}) => cached(
    lookupCacheKeys.rooms(params), '/Rooms', params, options,
  ),

  createRoom: async (payload) => afterMutation(
    unwrap(api.post('/Rooms', payload)), 'lookups:rooms',
  ),

  updateRoom: async (id, payload) => afterMutation(
    unwrap(api.put(`/Rooms/${id}`, payload)), 'lookups:rooms',
  ),

  removeRoom: async (id) => afterMutation(
    unwrap(api.delete(`/Rooms/${id}`)), 'lookups:rooms',
  ),

  getStatuses: async (params = {}, options = {}) => cached(
    lookupCacheKeys.statuses(params), '/Statuses', params, options,
  ),

  getSystemLanguages: async (params = {}, options = {}) => cached(
    lookupCacheKeys.systemLanguages(params), '/SystemLanguages', params, options,
  ),

  getTimezones: async (params = {}, options = {}) => cached(
    lookupCacheKeys.timezones(params), '/Timezone', params, options,
  ),

  getDateFormats: async (params = {}, options = {}) => cached(
    lookupCacheKeys.dateFormats(params), '/DateFormat', params, options,
  ),
};
